import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

// @mui material components
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import SEO from "components/SEO";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Payment section
import PaymentSection from "pages/Payment/sections/PaymentSection";

// API
import { getBooking } from "api";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

function PriceRow({ label, value, bold }) {
  return (
    <MKBox display="flex" justifyContent="space-between" mb={1}>
      <MKTypography
        variant="body2"
        fontWeight={bold ? "bold" : "regular"}
        color={bold ? "dark" : "text"}
      >
        {label}
      </MKTypography>
      <MKTypography
        variant="body2"
        fontWeight={bold ? "bold" : "regular"}
        color={bold ? "dark" : "text"}
      >
        ${value.toLocaleString()}
      </MKTypography>
    </MKBox>
  );
}

function Payment() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadBooking() {
      try {
        const data = await getBooking(bookingId);
        setBooking(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadBooking();
  }, [bookingId]);

  const renderContent = () => {
    if (loading) {
      return (
        <MKBox py={8} textAlign="center">
          <CircularProgress />
          <MKTypography variant="body2" color="text" mt={2}>
            Loading booking details...
          </MKTypography>
        </MKBox>
      );
    }

    if (error) {
      return (
        <MKBox py={8} textAlign="center">
          <Icon sx={{ fontSize: 64, color: "error.main", mb: 2 }}>error_outline</Icon>
          <MKTypography variant="h5" mb={1}>
            Booking Not Found
          </MKTypography>
          <MKTypography variant="body2" color="text" mb={3}>
            {error}
          </MKTypography>
          <MKButton variant="gradient" color="info" onClick={() => (window.location.href = "/")}>
            Back to Home
          </MKButton>
        </MKBox>
      );
    }

    if (booking.paymentStatus === "deposit_paid") {
      return (
        <MKBox py={8} textAlign="center">
          <Icon sx={{ fontSize: 64, color: "success.main", mb: 2 }}>check_circle</Icon>
          <MKTypography variant="h4" mb={2}>
            Already Paid
          </MKTypography>
          <MKTypography variant="body1" color="text" mb={1}>
            The deposit for this booking has already been paid.
          </MKTypography>
          <MKTypography variant="body2" color="text" mb={3}>
            {booking.checkIn} to {booking.checkOut} &middot; {booking.guestName}
          </MKTypography>
          <MKButton variant="gradient" color="info" onClick={() => (window.location.href = "/")}>
            Back to Home
          </MKButton>
        </MKBox>
      );
    }

    const { pricing } = booking;
    const bal = pricing.balanceDue || pricing.total - pricing.minimumDeposit;
    const cashPct = Math.round((pricing.cashDiscountPercent || 0.06) * 100);
    const cashBal = pricing.balanceCash || Math.round(bal * 0.94);
    const depPct = Math.round((pricing.depositPercent || 0.5) * 100);

    return (
      <>
        {/* Booking Summary */}
        <MKBox component="section" py={4}>
          <Container maxWidth="sm">
            <MKTypography variant="h5" mb={2}>
              Booking Summary
            </MKTypography>
            <MKBox mb={2}>
              <MKTypography variant="body2" color="text">
                <strong>Guest:</strong> {booking.guestName}
              </MKTypography>
              <MKTypography variant="body2" color="text">
                <strong>Dates:</strong> {booking.checkIn} to {booking.checkOut}
              </MKTypography>
            </MKBox>

            <MKBox bgColor="grey-100" borderRadius="xl" p={3}>
              <PriceRow
                label={`$${Math.round(pricing.accommodationTotal / pricing.nights)} x ${
                  pricing.nights
                } night${pricing.nights > 1 ? "s" : ""}`}
                value={pricing.accommodationTotal}
              />
              <PriceRow label="Cleaning fee" value={pricing.cleaningFee} />
              <PriceRow
                label={`VAT (${Math.round((pricing.vatPercent || 0.13) * 100)}%)`}
                value={pricing.vat || pricing.serviceFee || 0}
              />
              <Divider sx={{ my: 2 }} />
              <PriceRow label="Total" value={pricing.total} bold />
              <Divider sx={{ my: 2 }} />
              <MKBox bgColor="white" borderRadius="lg" p={2} mt={1}>
                <PriceRow
                  label={`Deposit due now (${depPct}%)`}
                  value={pricing.minimumDeposit}
                  bold
                />
                <Divider sx={{ my: 1.5 }} />
                <MKTypography variant="button" fontWeight="bold" mb={1} display="block">
                  Balance on arrival
                </MKTypography>
                <MKTypography variant="body2" color="success" fontWeight="medium" mb={0.5}>
                  💵 Cash: ${cashBal.toLocaleString()} (save {cashPct}%)
                </MKTypography>
                <MKTypography variant="body2" fontWeight="medium">
                  💳 Card: ${bal.toLocaleString()}
                </MKTypography>
              </MKBox>
            </MKBox>
          </Container>
        </MKBox>

        {/* Payment */}
        <PaymentSection
          bookingId={bookingId}
          depositAmount={pricing.minimumDeposit}
          isTestPayment={booking.isTestPayment}
        />
      </>
    );
  };

  return (
    <>
      <SEO noindex title="Complete Your Payment" path="/payment" />
      <DefaultNavbar routes={routes} sticky />
      <MKBox
        minHeight="40vh"
        width="100%"
        variant="gradient"
        bgColor="info"
        sx={{
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <MKTypography variant="h2" component="h1" color="white" textAlign="center">
            Complete Your Payment
          </MKTypography>
          <MKTypography variant="body1" color="white" textAlign="center" opacity={0.8} mt={1}>
            Secure your reservation with a deposit payment
          </MKTypography>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {renderContent()}
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

import PropTypes from "prop-types";

PriceRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  bold: PropTypes.bool,
};

export default Payment;
